/**
 * ============================================================================
 * KbPageReader — Renderiza content_json (Tiptap doc) como HTML estático,
 *                usando as MESMAS extensions do editor + tipografia `.kb-prose`.
 *                Serve para:
 *                  - Aba "Visualizar" no editor
 *                  - ExtranetPagePage
 *                  - KB pública
 *                Elimina divergência editor ↔ leitor.
 * ----------------------------------------------------------------------------
 * Implementação: usa `generateHTML` do Tiptap (sync, sem instanciar editor),
 *                assim o custo é mínimo — ideal para páginas públicas.
 *
 *                Renderiza também o KbPageLinkHydrator (client-side) para
 *                enriquecer <a data-kb-page-link> com título/estado ao vivo.
 *                Não é executado quando `disableHydration` (SSR/prints).
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.1.0 - Suporte a wiki-links (data-kb-page-link) e
 *            hidratação client-side opcional; preserva ids de heading.
 *   RELEASE: 2026-07-08 v1.0.1 - Preserva `data-active` e `data-label` no
 *            HTML sanitizado para manter tabs estáticas fiéis ao editor.
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useMemo, useRef } from "react";
import { generateHTML } from "@tiptap/html";
import DOMPurify from "dompurify";
import { KB_TIPTAP_EXTENSIONS } from "@/components/kb/editor/kbTiptapExtensions";
import { KbPageLinkHydrator } from "@/components/kb/editor/KbPageLinkHydrator";

interface Props {
  /** Documento Tiptap (JSON) — geralmente `page.content_json` */
  contentJson: unknown;
  /** Fallback: renderiza texto plano se JSON inválido ou vazio */
  fallbackText?: string | null;
  /** Classe extra (padrão já aplica `.kb-prose`) */
  className?: string;
  /** Desativa hidratação de wiki-links (útil em SSR/impressão/print) */
  disableHydration?: boolean;
  /** Abre wiki-links em nova aba (público/extranet) */
  openLinksInNewTab?: boolean;
}

export function KbPageReader({
  contentJson,
  fallbackText,
  className,
  disableHydration,
  openLinksInNewTab,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const html = useMemo(() => {
    try {
      if (!contentJson || typeof contentJson !== "object") return null;
      const doc = contentJson as { type?: string; content?: unknown[] };
      if (doc.type !== "doc") return null;
      const raw = generateHTML(doc as any, KB_TIPTAP_EXTENSIONS as any);
      // Sanitiza (páginas podem vir de importação de .md, extranet etc.)
      return DOMPurify.sanitize(raw, {
        ADD_TAGS: ["iframe", "details", "summary"],
        // Mantém os atributos estruturais dos blocos ricos (tabs/callout/etc.)
        // para que o HTML estático preserve a mesma visibilidade do editor.
        // Preserva também os atributos usados por wiki-links (KbPageLink).
        ADD_ATTR: [
          "allow", "allowfullscreen", "frameborder", "target",
          "data-type", "data-variant", "data-active", "data-label",
          "data-kb-page-link", "data-page-id", "data-space-id",
          "data-anchor", "data-snapshot-title", "data-kb-state",
          "id",
        ],
      });
    } catch {
      return null;
    }
  }, [contentJson]);

  if (!html) {
    return (
      <div className={`kb-prose ${className ?? ""}`}>
        <p className="text-muted-foreground">{fallbackText ?? "Sem conteúdo."}</p>
      </div>
    );
  }

  return (
    <>
      <div
        ref={containerRef}
        className={`kb-prose max-w-none ${className ?? ""}`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {!disableHydration && (
        <KbPageLinkHydrator
          containerRef={containerRef}
          openInNewTab={openLinksInNewTab}
          observeKey={html}
        />
      )}
    </>
  );
}
